import React, { useEffect, useMemo, useState } from 'react';
const logo = process.env.PUBLIC_URL + '/images/logo.png';
// @ts-ignore - CSS import for react-big-calendar
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Calendar, dateFnsLocalizer, Views } from 'react-big-calendar';
import { format, parse, startOfWeek, getDay } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Link, useNavigate } from 'react-router-dom';
import { getArtistBookings } from '../../services/bookingService';
import { getPaymentsByBooking } from '../../services/paymentService';
import { safeFormatDate } from '../../common/dateUtils';
import { calculateBookingTotal } from '../../utils/bookingCalculations';
import { getMakeupArtist } from '../../services/makeupArtistService';
import Footer from '../../components/Footer';

export default function Dashboard() {
	const [bookings, setBookings] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);
	const [paidTotal, setPaidTotal] = useState(0);

		const [artistName, setArtistName] = useState<string>('Artist');
		useEffect(() => {
			const storedName = (typeof window !== 'undefined') ? localStorage.getItem('makeupArtistName') : null;
			if (storedName) {
				setArtistName(storedName);
			} else if (typeof window !== 'undefined') {
				const idStr = localStorage.getItem('makeupArtistId');
				const id = idStr ? Number(idStr) : 0;
				if (id > 0) {
					getMakeupArtist(id).then((a: any) => {
						if (a?.displayName) {
							setArtistName(a.displayName);
							if (typeof window !== 'undefined') {
								localStorage.setItem('makeupArtistName', a.displayName);
							}
						}
					}).catch(() => {});
				}
			}
		}, []);
	// Live clock to keep the dashboard feeling fresh
	const [now, setNow] = useState<Date>(new Date());
	useEffect(() => {
		const t = setInterval(() => setNow(new Date()), 30000);
		return () => clearInterval(t);
	}, []);

	const greeting = useMemo(() => {
		const h = now.getHours();
		if (h < 12) return 'Good morning';
		if (h < 18) return 'Good afternoon';
		return 'Good evening';
	}, [now]);

	// Lightweight rotating tips for a more interactive feel
	const tips = [
		'Tip: Add booking items inline while creating a booking.',
		'Did you know? You can track payments and see due amounts instantly.',
		'Pro: Maintain your own outsource team under My Outsource Persons.',
		'Heads up: Upcoming list hides canceled bookings automatically.',
	];
	const [tipIndex, setTipIndex] = useState(0);
	const [showTips, setShowTips] = useState(true);
	useEffect(() => {
		if (!showTips) return;
		const t = setInterval(() => {
			setTipIndex((i) => (i + 1) % tips.length);
		}, 8000);
		return () => clearInterval(t);
	}, [showTips, tips.length]);

	useEffect(() => {
		const load = async () => {
			try {
				setLoading(true);
				const data = await getArtistBookings();
				setBookings(data || []);
				// Compute paid totals by fetching payments per booking
				const paymentsList = await Promise.all(
					(data || []).map(async (b: any) => {
						try {
							const payments = await getPaymentsByBooking(b.id);
							return payments.reduce((s: number, p: any) => s + (Number(p.paidAmount) || 0), 0);
						} catch {
							return 0;
						}
					})
				);
				const totalPaid = paymentsList.reduce((s, n) => s + n, 0);
				setPaidTotal(totalPaid);
			} finally {
				setLoading(false);
			}
		};
		load();
	}, []);

	const totals = useMemo(() => {
		// Use calculateBookingTotal for consistent calculation across the app
		const computeBookingAmount = (b: any) => {
			const explicit = Number(b?.bookingAmount);
			if (Number.isFinite(explicit) && explicit > 0) return explicit;
			return calculateBookingTotal(b?.bookingItems || []);
		};
		const bookingAmountTotal = bookings.reduce((s, b) => s + computeBookingAmount(b), 0);
		const pending = Math.max(0, bookingAmountTotal - paidTotal);
		return { bookingAmountTotal, paidTotal, pending };
	}, [bookings, paidTotal]);

	const counts = useMemo(() => {
		const toKey = (s: string) => (s || '').toLowerCase();
		let enquired = 0, booked = 0, canceled = 0;
		for (const b of bookings) {
			const k = toKey(b.status);
			if (k === 'enquired') enquired++;
			else if (k === 'booked' || k === 'confirmed') booked++;
			else if (k === 'canceled' || k === 'cancelled') canceled++;
		}
		return { enquired, booked, canceled };
	}, [bookings]);

	const upcoming = useMemo(() => {
			const today = new Date();
			return bookings
				.filter(b => {
					if (!b.eventDate) return false;
					const d = new Date(b.eventDate);
					const isFutureOrToday = !isNaN(d.getTime()) && d >= new Date(today.toDateString());
					const status = (b.status || '').toLowerCase();
					const isCanceled = status === 'canceled' || status === 'cancelled';
					return isFutureOrToday && !isCanceled;
				})
			.sort((a, b) => new Date(a.eventDate).getTime() - new Date(b.eventDate).getTime())
			.slice(0, 5);
	}, [bookings]);

	const navigate = useNavigate();

	// Calendar localizer (date-fns)
	const locales = { 'en-US': enUS } as const;
	const localizer = useMemo(() => dateFnsLocalizer({ format, parse, startOfWeek, getDay, locales }), []);

	// Build calendar events from booking items per date; fallback to eventDate
	const calendarEvents = useMemo(() => {
		const events: Array<{ title: string; start: Date; end: Date; allDay?: boolean; resource?: any }> = [];
		for (const b of bookings) {
			const clientName = b?.client?.fullName || 'Client';
			const items = Array.isArray(b?.bookingItems) ? b.bookingItems.filter((it: any) => !it?.isDeleted) : [];
			const byDate: Record<string, number> = {};
			for (const it of items) {
				const d = it?.bookingDate ? new Date(it.bookingDate) : null;
				if (!d || isNaN(d.getTime())) continue;
				const key = d.toISOString().slice(0, 10);
				byDate[key] = (byDate[key] || 0) + 1;
			}
			// Fallback to eventDate if no item dates present
			if (Object.keys(byDate).length === 0) {
				const d = b?.eventDate ? new Date(b.eventDate) : null;
				if (d && !isNaN(d.getTime())) {
					const count = items.length > 0 ? items.length : 1;
					const dayStart = new Date(d.getFullYear(), d.getMonth(), d.getDate());
					events.push({
						title: `${clientName} (${count})`,
						start: dayStart,
						end: new Date(dayStart.getTime() + 60 * 60 * 1000),
						allDay: true,
						resource: { bookingId: b.id }
					});
				}
			} else {
				for (const key of Object.keys(byDate)) {
					const [yyyy, mm, dd] = key.split('-').map((n) => Number(n));
					const dayStart = new Date(yyyy, mm - 1, dd);
					events.push({
						title: `${clientName} (${byDate[key]})`,
						start: dayStart,
						end: new Date(dayStart.getTime() + 60 * 60 * 1000),
						allDay: true,
						resource: { bookingId: b.id }
					});
				}
			}
		}
		return events;
	}, [bookings]);
	return (
		<div className="p-6 pb-20 bg-background min-h-screen font-sans">
			{/* Welcome */}
					<div className="mb-6">
						<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
							<div>
								<h1 className="text-3xl font-semibold text-primary font-sans tracking-tight">Welcome to {artistName} Panel</h1>
								<p className="text-secondary">{now.toLocaleString()}</p>
							</div>
							  <span className="inline-block self-start sm:self-auto bg-highlight text-secondary text-xs font-medium px-3 py-1 rounded-full">
								{greeting}
							</span>
						</div>
						{showTips && (
							<div className="mt-3 flex items-start justify-between gap-3 bg-white border border-highlight rounded-2xl p-3 shadow">
								<div className="text-sm text-secondary">
									{tips[tipIndex]}
								</div>
								<button
									className="text-xs text-primary hover:underline"
									onClick={() => setShowTips(false)}
								>
									Hide tips
								</button>
							</div>
						)}
					</div>

			{/* KPIs */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
				<div className="bg-white shadow rounded p-4">
					  <div className="text-secondary text-sm">Enquired</div>
					<div className="text-3xl font-semibold text-yellow-600">{counts.enquired}</div>
				</div>
				<Link to="/artist/bookings?status=booked" className="bg-white shadow rounded p-4 block hover:shadow-md transition">
					  <div className="text-secondary text-sm">Booked</div>
					<div className="text-3xl font-semibold text-green-600">{counts.booked}</div>
				</Link>
				<Link to="/artist/bookings?status=canceled" className="bg-white shadow rounded p-4 block hover:shadow-md transition">
					  <div className="text-secondary text-sm">Canceled</div>
					<div className="text-3xl font-semibold text-red-600">{counts.canceled}</div>
				</Link>
			</div>

			{/* Financials */}
			<div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
				<div className="bg-white shadow rounded p-4">
					  <div className="text-secondary text-sm">Booking Amount</div>
					<div className="text-2xl font-semibold">₹ {totals.bookingAmountTotal.toFixed(2)}</div>
				</div>
				<div className="bg-white shadow rounded p-4">
					  <div className="text-secondary text-sm">Paid Amount</div>
					<div className="text-2xl font-semibold text-green-700">₹ {totals.paidTotal.toFixed(2)}</div>
				</div>
				<div className="bg-white shadow rounded p-4">
					  <div className="text-secondary text-sm">Pending Amount</div>
					<div className="text-2xl font-semibold text-red-700">₹ {totals.pending.toFixed(2)}</div>
				</div>
			</div>

			{/* Bookings Calendar */}
			<div className="bg-white shadow rounded p-4 mb-6">
				<div className="flex items-center justify-between mb-3">
														<h2 className="text-lg font-semibold mb-1">Bookings Calendar</h2>
					  {loading && <span className="text-sm text-secondary">Loading...</span>}
				</div>
				<div className="h-[420px] md:h-[650px]">
					<Calendar
						localizer={localizer}
						events={calendarEvents}
						startAccessor="start"
						endAccessor="end"
						views={[Views.MONTH, Views.WEEK, Views.DAY]}
						defaultView={Views.MONTH}
						popup
						style={{ height: '100%' }}
						onSelectEvent={(ev: any) => {
							const id = ev?.resource?.bookingId;
							if (id) navigate(`/artist/bookings?open=${id}`);
						}}
						tooltipAccessor={(ev: any) => ev?.title}
					/>
				</div>
			</div>

			{/* Upcoming bookings */}
			<div className="bg-white shadow rounded p-4">
				<div className="flex items-center justify-between mb-3">
														<h2 className="text-lg font-semibold mb-1">Upcoming Bookings</h2>
					  {loading && <span className="text-sm text-secondary">Loading...</span>}
				</div>
				{upcoming.length === 0 ? (
					  <p className="text-secondary">No upcoming bookings.</p>
				) : (
					<div className="overflow-x-auto">
						<table className="min-w-full">
							  <thead className="text-left text-secondary text-sm bg-highlight">
								<tr>
											<th className="py-2">Dates</th>
									<th className="py-2">Client</th>
									<th className="py-2">Venue</th>
									<th className="py-2">Amount</th>
											<th className="py-2">Total Service Count</th>
									<th className="py-2">Status</th>
								</tr>
							</thead>
							<tbody>
												{upcoming.map((b) => (
									<tr
										key={b.id}
										  className="border-t hover:bg-highlight/40 cursor-pointer"
										onClick={() => navigate(`/artist/bookings?open=${b.id}`)}
									>
												<td className="py-2">
													{(() => {
														// Collect distinct item dates; fallback to eventDate if none
														const rawDates: string[] = Array.isArray(b?.bookingItems)
															? b.bookingItems
																.filter((it: any) => !it?.isDeleted)
																.map((it: any) => (it?.bookingDate ? new Date(it.bookingDate) : null))
																.filter((d: Date | null) => d && !isNaN(d.getTime()))
																.map((d: Date) => d.toISOString().slice(0, 10))
															: [];
														const uniq = Array.from(new Set(rawDates));
														const display = (uniq.length > 0 ? uniq : [b.eventDate])
															.filter(Boolean)
															.map((d: any, i: number) => (
																  <span key={String(d)+i} className="inline-block bg-highlight text-secondary text-xs font-medium px-2 py-0.5 rounded mr-1 mb-1">
																	{safeFormatDate(d)}
																</span>
															));
														return <div className="flex flex-wrap">{display}</div>;
													})()}
												</td>
										<td className="py-2">{b.client?.fullName || '-'}</td>
										<td className="py-2">{b.venue || '-'}</td>
														<td className="py-2">₹ {(() => {
																const explicit = Number(b?.bookingAmount);
																if (Number.isFinite(explicit) && explicit > 0) return explicit.toFixed(2);
																return calculateBookingTotal(b?.bookingItems || []).toFixed(2);
														})()}</td>
												<td className="py-2">{Array.isArray(b?.bookingItems) ? b.bookingItems.filter((it: any) => !it?.isDeleted).reduce((sum: number, it: any) => sum + (Number(it.serviceCount) || 1), 0) : 0}</td>
										<td className="py-2">{b.status}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</div>
			<Footer />
		</div>
	);
}